import { useEffect, useState } from "react";
import Icon from "./Icon";

/**
 * Checkbox component
 *
 * Renders a customizable checkbox with optional label, disabled state,
 * background styling, and controlled/uncontrolled behavior through props.
 *
 * @param {object} props
 * @param {string} props.id
 *   The unique ID for the checkbox input element.
 *
 * @param {string} [props.label]
 *   The text label displayed next to the checkbox.
 *
 * @param {boolean} [props.enabled=true]
 *   Whether the checkbox is interactive. When `false`, it is disabled.
 *
 * @param {boolean} [props.showBackground=false]
 *   Whether to display a background box around the checkbox container.
 *
 * @param {(event: React.ChangeEvent<HTMLInputElement>) => void} props.onChange
 *   Callback fired when the checkbox state changes.
 *
 * @param {boolean} props.checked
 *   Controlled checked value passed from the parent component.
 */
export default function Checkbox({
  id,
  label,
  enabled = true,
  showBackground = false,
  onChange,
  checked: checkedProp,
}) {
  const [checked, setChecked] = useState(Boolean(checkedProp));

  useEffect(() => {
    setChecked(Boolean(checkedProp));
  }, [checkedProp]);

  const labelClass = enabled
    ? "text-main-typography-system-default"
    : "text-main-typography-system-caption";

  const styleClass = showBackground
    ? "bg-creation-neutral-container-surface-default rounded-md"
    : "";

  const handleChange = (e) => {
    if (!enabled) return;
    setChecked(e.target.checked);
    if (onChange) onChange(e);
  };

  return (
    <div className={`inline-flex p-2 ${styleClass}`}>
      <label className="flex items-start cursor-pointer relative">
        <input
          type="checkbox"
          id={id}
          name={id}
          className="appearance-none peer absolute opacity-0 w-0 h-0"
          checked={checked}
          onChange={handleChange}
          disabled={!enabled}
        />
        <div
          className={`
            w-scale-md h-scale-md border rounded-sm flex items-center justify-center flex-shrink-0
            bg-creation-neutral-container-surface-default 
            border-main-border-system-default text-transparent 
            peer-hover:bg-creation-primary-container-surface-hovered
            peer-hover:border-creation-neutral-container-on-surface-hovered
              peer-hover:text-transparent
              peer-disabled:bg-creation-neutral-container-surface-disabled 
              peer-disabled:border-creation-neutral-solid-on-surface-disabled 
              peer-disabled:text-transparent 
              peer-checked:bg-creation-primary-solid-surface-focusing 
              peer-checked:border-transparent 
              peer-checked:text-creation-primary-solid-on-surface-focusing 
              peer-hover:peer-checked:bg-creation-primary-solid-surface-hovered
              peer-hover:peer-checked:border-transparent
              peer-hover:peer-checked:text-creation-primary-solid-on-surface-hovered
              peer-disabled:peer-checked:bg-creation-primary-container-surface-disabled 
              peer-disabled:peer-checked:border-transparent 
              peer-disabled:peer-checked:text-creation-primary-container-on-surface-disabled
          `}
        >
          <Icon name="check" size="xs" />
        </div>
      </label>
      {label && (
        <span className={`flex items-center px-2 font-ui font-body text-body-base ${labelClass}`}>
          {label}
        </span>
      )}
    </div>
  );
}
